import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import ToggleSwitch from "@/components/base/ToggleSwitch";

interface EmailPrefs {
  marketing_emails: boolean;
  offers: boolean;
  new_products: boolean;
  order_emails: boolean;
}

interface EmailPreferencesProps {
  userId: string;
}

interface ToggleRow {
  key: keyof EmailPrefs;
  label: string;
  description: string;
  icon: string;
}

const ROWS: ToggleRow[] = [
  {
    key: "marketing_emails",
    label: "Emails de marketing",
    description: "Comunicaciones promocionales generales.",
    icon: "ri-megaphone-line",
  },
  {
    key: "offers",
    label: "Ofertas",
    description: "Descuentos, rebajas y ofertas especiales.",
    icon: "ri-price-tag-3-line",
  },
  {
    key: "new_products",
    label: "Nuevos productos",
    description: "Avisos cuando lleguen productos nuevos.",
    icon: "ri-box-3-line",
  },
  {
    key: "order_emails",
    label: "Notificaciones de pedidos",
    description: "Estado de tus pedidos (enviado, entregado, etc.).",
    icon: "ri-truck-line",
  },
];

/**
 * Preferencias de email del usuario. Permite controlar correos comerciales,
 * pero los correos críticos (seguridad, pagos, recuperación de contraseña)
 * nunca se desactivan.
 */
export default function EmailPreferences({ userId }: EmailPreferencesProps) {
  const [prefs, setPrefs] = useState<EmailPrefs>({
    marketing_emails: true,
    offers: true,
    new_products: true,
    order_emails: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("email_preferences")
        .select("marketing_emails, offers, new_products, order_emails")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setPrefs(data as EmailPrefs);
      }
    } catch (err) {
      showToast(
        err instanceof Error
          ? err.message
          : "No se pudieron cargar las preferencias.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [userId, showToast]);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  async function toggle(key: keyof EmailPrefs) {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      const { error } = await supabase
        .from("email_preferences")
        .upsert({ user_id: userId, [key]: next[key] }, { onConflict: "user_id" });
      if (error) throw error;
    } catch (err) {
      setPrefs(prefs);
      showToast(
        err instanceof Error
          ? err.message
          : "No se pudo actualizar la preferencia.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <p className="text-sm leading-relaxed text-foreground-600">
        Elegí qué correos querés recibir. Los correos de seguridad, pagos y
        recuperación de contraseña no se pueden desactivar.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-background-200/60 overflow-hidden rounded-lg border border-background-200/70">
          {ROWS.map((row) => {
            const active = prefs[row.key];
            return (
              <li
                key={row.key}
                className="flex items-center gap-4 bg-background-50 p-4"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100">
                  <i className={`${row.icon} text-lg text-secondary-600`}></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900">
                    {row.label}
                  </p>
                  <p className="text-xs text-foreground-500">
                    {row.description}
                  </p>
                </div>
                <ToggleSwitch
                  checked={active}
                  onChange={() => toggle(row.key)}
                  disabled={saving}
                />
              </li>
            );
          })}
          <li className="flex items-center gap-4 bg-background-100/60 p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100">
              <i className="ri-shield-check-line text-lg text-accent-600"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground-900">
                Emails de seguridad
              </p>
              <p className="text-xs text-foreground-500">
                Siempre activos. No se pueden desactivar.
              </p>
            </div>
            <ToggleSwitch checked onChange={() => {}} disabled label="Emails de seguridad" />
          </li>
        </ul>
      )}
    </div>
  );
}